import './styles.scss';

import { ComboBox } from 'src/components/ui/combobox';
import { formatSIN } from 'src/utils/formatInputs';
import { FundingService } from 'src/services/funding.service';
import { isFirstRender } from 'src/utils/isFirstMount';
import { isSiteCanadian } from 'src/utils/helpers';
import TooltipIcon from 'src/components/ui/tooltip';
import { useLocalization } from 'src/utils/useLocalization';
import { useTranslation } from 'react-i18next';
import {
    FundingSearchTypes,
    FundingStatusTableData,
} from 'src/services/models/funding.service';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { matchAccountNumber, matchSSN, matchVIN } from 'src/utils/validators';
interface ISearchProps {
    updateTableContent: Dispatch<SetStateAction<FundingStatusTableData[]>>;
    dealerID: string;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setFailedSearchTerm: Dispatch<SetStateAction<string>>;
}

const search = {
    [FundingSearchTypes.SSN]: matchSSN,
    [FundingSearchTypes.VIN]: matchVIN,
    [FundingSearchTypes.AccountNumber]: matchAccountNumber,
};

/** US SSN typed without dashes becomes ###-##-####. */
export const normalizeSearchTerm = (searchText: string): string =>
    !isSiteCanadian() && /^\d{9}$/.test(searchText)
        ? formatSIN(searchText)
        : searchText;

export const parseSearch = (searchText: string): FundingSearchTypes => {
    if (searchText === '') {
        return FundingSearchTypes.ReceiveInDays;
    }

    for (const [searchType, validator] of Object.entries(search)) {
        if (validator(searchText)) {
            return searchType as FundingSearchTypes;
        }
    }
    return FundingSearchTypes.ApplicantName;
};

export const Search: React.FC<ISearchProps> = ({
    updateTableContent,
    dealerID,
    setLoading,
    setFailedSearchTerm,
}) => {
    const {
        creditSearch,
        fundingStatus,
        statusFilterOptionToResponseMap,
        statusFilterOptionMap,
    } = useLocalization();
    const { t, i18n } = useTranslation();

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [dateReceived, setDateReceived] = useState<string>('');
    const [resetState, setResetState] = useState<boolean>(false);

    const firstRender = isFirstRender();

    const service = new FundingService();

    const statusFilterOptions: string[] = [
        fundingStatus.TABLE_FUNCTION_ALL_STATUSES,
        fundingStatus.BOOKED,
        fundingStatus.RECEIVED,
        fundingStatus.UNABLETOFUND,
        fundingStatus.WITHDRAWN,
        fundingStatus.APPROVED,
    ];

    enum FilterNames {
        Search = 'Search',
        Status = 'Status',
        Date = 'Date',
    }

    const resetFilters = async (filtersToReset: FilterNames[]) => {
        const all = !filtersToReset.length;
        setResetState(true);
        (!!all || !!filtersToReset.includes(FilterNames.Search)) &&
            setSearchTerm('');
        (!!all || !!filtersToReset.includes(FilterNames.Status)) &&
            setStatusFilter('');
        (!!all || !!filtersToReset.includes(FilterNames.Date)) &&
            setDateReceived('');
    };

    const handleReset = async () => {
        await resetFilters([]);
        setResetState(false);
        searchDefaultData();
    };

    const searchDefaultData = async () => {
        setLoading(true);
        const DEFAULT_SEARCH_TIME = '90';
        setDateReceived(DEFAULT_SEARCH_TIME);
        try {
            const response = await service.fundingStatusSearch(
                dealerID,
                FundingSearchTypes.ReceiveInDays,
                DEFAULT_SEARCH_TIME,
            );
            if (!!response && !!response.data) {
                updateTableContent(service.mapResponse(response.data));
                setLoading(false);
            }
        } catch {
            updateTableContent([]);
            i18n.on('languageChanged', function () {
                setFailedSearchTerm(
                    `${dateReceived} ${t('Funding.Status.Table.Option.Days')}`,
                );
            });
            setFailedSearchTerm(
                `${dateReceived} ${fundingStatus.TABLE_OPTION_DAYS}`,
            );
            setLoading(false);
        }
    };

    const handleStatusFilterChange = async () => {
        await resetFilters([FilterNames.Date, FilterNames.Search]);
        setResetState(false);
        setLoading(true);
        let response;
        try {
            if (statusFilter === fundingStatus.TABLE_FUNCTION_ALL_STATUSES) {
                response = await service.fundingStatusSearch(
                    dealerID,
                    FundingSearchTypes.ReceiveInDays,
                    '90',
                );
            } else {
                response = await service.fundingStatusSearch(
                    dealerID,
                    FundingSearchTypes.Status,
                    statusFilterOptionToResponseMap[statusFilter],
                );
            }
            if (!!response && !!response.data) {
                updateTableContent(service.mapResponse(response.data));
                setLoading(false);
            }
        } catch {
            updateTableContent([]);
            setFailedSearchTerm(statusFilter);
            i18n.on('languageChanged', function () {
                setFailedSearchTerm(t(statusFilterOptionMap[statusFilter]));
            });
            setLoading(false);
        }
    };

    const handleDateReceivedChange = async () => {
        await resetFilters([FilterNames.Status, FilterNames.Search]);
        setResetState(false);
        setLoading(true);
        try {
            const response = await service.fundingStatusSearch(
                dealerID,
                FundingSearchTypes.ReceiveInDays,
                dateReceived,
            );
            if (!!response && !!response.data) {
                updateTableContent(service.mapResponse(response.data));
                setLoading(false);
            }
        } catch {
            updateTableContent([]);
            i18n.on('languageChanged', function () {
                setFailedSearchTerm(
                    `${dateReceived} ${t('Funding.Status.Table.Option.Days')}`,
                );
            });
            setFailedSearchTerm(
                `${dateReceived} ${fundingStatus.TABLE_OPTION_DAYS}`,
            );
            setLoading(false);
        }
    };

    const fundingSearchRequest = async () => {
        await resetFilters([FilterNames.Status, FilterNames.Date]);
        setResetState(false);
        setLoading(true);
        const normalizedTerm = normalizeSearchTerm(searchTerm);
        const searchType = parseSearch(normalizedTerm);
        try {
            const res = await service.fundingStatusSearch(
                dealerID,
                searchType,
                normalizedTerm,
            );
            if (!!res && !!res.data) {
                const tableItems = service.mapResponse(res.data);
                updateTableContent(tableItems);
                setLoading(false);
            }
        } catch {
            updateTableContent([]);
            setFailedSearchTerm(searchTerm);
            setLoading(false);
        }
    };

    useEffect(() => {
        searchDefaultData();
    }, []);

    const shouldMakeRequest: boolean = !firstRender && !resetState;

    useEffect(() => {
        if (shouldMakeRequest) {
            handleDateReceivedChange();
        }
    }, [dateReceived]);

    useEffect(() => {
        if (shouldMakeRequest) {
            handleStatusFilterChange();
        }
    }, [statusFilter]);

    const handleEnter = (evt: any) => {
        if (evt.key === 'Enter') {
            fundingSearchRequest();
        }
    };

    const tooltip = isSiteCanadian()
        ? fundingStatus.TABLE_FUNCTION_SEARCH_BY_CA
        : fundingStatus.TABLE_FUNCTION_SEARCH_BY;

    return (
        <div className='funding-search-bar search-row c-input'>
            <div className='search-div'>
                <div className='c-input search-text'>
                    <span id='searchIcon'>
                        <i
                            className='c-icon c-icon--[semantic-search]'
                            aria-hidden='true'
                            role={'img'}
                        ></i>
                    </span>
                    <input
                        id='searchInput'
                        className='c-input__input'
                        type={'text'}
                        value={searchTerm}
                        placeholder={creditSearch.SEARCH_BAR_PLACEHOLDER}
                        onChange={(evt) =>
                            setSearchTerm(evt.target.value.trim())
                        }
                        onKeyDown={handleEnter}
                        role='searchQuery'
                    />
                    <span
                        id='clearSearch'
                        className={`c-input__addon ${
                            !searchTerm ? 'invisible' : ''
                        }`}
                        onClick={() => handleReset()}
                        style={{ borderColor: '#d1dcde' }}
                        data-testid='clearSearch'
                        role='clearSearch'
                    >
                        <i
                            className='c-icon  c-icon--[semantic-close]'
                            aria-hidden='true'
                            role={'img'}
                        ></i>
                    </span>
                </div>
                <TooltipIcon text={tooltip}></TooltipIcon>
            </div>
            <div className='select-status'>
                <ComboBox
                    name={'status'}
                    options={statusFilterOptions}
                    defaultValue={''}
                    placeholder={fundingStatus.TABLE_FUNCTION_ALL_STATUSES}
                    onSelect={setStatusFilter}
                    selectedOption={statusFilter}
                />
            </div>
            <div className='select-date-range'>
                <ComboBox
                    name={'date_range'}
                    options={['7', '14', '30', '60', '90']}
                    defaultValue={''}
                    placeholder={fundingStatus.TABLE_FUNCTION_RECEIVED_DATE}
                    selectedOption={dateReceived}
                    onSelect={setDateReceived}
                    optionModifier={fundingStatus.TABLE_OPTION_DAYS}
                    shouldDisplayOptionName={true}
                />
            </div>
            <div className='reset-search'>
                <button
                    className={'c-btn--secondary c-btn c-btn--small'}
                    type={'button'}
                    onClick={handleReset}
                >
                    <span className='c-btn__text'>
                        {fundingStatus.TABLE_FUNCTION_RESET_FILTER}
                    </span>
                </button>
            </div>
        </div>
    );
};

export default Search;
